import { IChatMessage } from '../interfaces/chat-message.interface';
import { EnumMessageRole } from '../enums/message-role.enum';
import {
  EnumHITLDecisionType,
  EnumHITLStatus,
  IHITLDecision,
  IHITLRequest
} from '../interfaces/hitl.interface';
import { IChatInputMessage } from '../interfaces/chat-input.interface';
import {
  isKnowledgeDocumentReference,
  toConfirmedReference,
  toOptionalReferences
} from '../interfaces/message-reference.interface';
import { generateLocalId } from '../utils/local-id.util';
import { IAyaEnvelope } from '../interfaces/aya-stream.interface';
import {
  isAgentAttachment,
  isUserAttachment
} from '../interfaces/message-attachment.interface';
import { EnumAttachmentStatus } from '../enums/attachment-status.enum';
import { IUserAttachment } from '../interfaces/user-attachment.interface';
import {
  normalizeAttachment,
  normalizeChart
} from '../utils/normalize-artifact.util';
import { ChatMessagesStore } from './chat-messages.store';
import { MarkdownFormatterService } from './markdown-formatter.service';
import { ResponseBuffer } from './response-buffer';

export class ConversationMessageProjection {
  private responseBuffers = new Map<string, ResponseBuffer>();
  private artifactIds = new Map<string, Set<string>>();

  constructor(
    private messagesStore: ChatMessagesStore,
    private markdownFormatter: MarkdownFormatterService
  ) {}

  clear(): void {
    this.responseBuffers.clear();
    this.artifactIds.clear();
  }

  appendDelta(envelope: IAyaEnvelope, placeholderId: string): void {
    const text = envelope.payload.text;
    if (typeof text !== 'string' || !text) {
      return;
    }
    const buffer = this.bufferFor(envelope, placeholderId);
    const rendered = buffer.append(text);
    this.messagesStore.patchById(buffer.messageId, (message) => {
      // Um replay completo pode começar antes do parcial já exibido pelo histórico.
      if (
        message.content.length > rendered.content.length &&
        message.content.startsWith(rendered.content)
      ) {
        return {};
      }
      return rendered;
    });
  }

  replaceText(envelope: IAyaEnvelope, placeholderId: string): void {
    const text = envelope.payload.text;
    if (typeof text !== 'string') {
      return;
    }
    const buffer = this.bufferFor(envelope, placeholderId);
    this.messagesStore.patchById(buffer.messageId, () => buffer.replace(text));
  }

  releaseResponse(placeholderId: string): void {
    const target = this.messagesStore
      .getMessages()
      .find((m) => m.localId === placeholderId || m.id === placeholderId);
    const messageId = target?.id || placeholderId;
    // A cadência de render pode ter deixado o último trecho sem converter.
    const buffer = this.responseBuffers.get(messageId);
    if (buffer?.hasPendingRender) {
      this.messagesStore.patchById(messageId, () => buffer.flush());
    }
    this.responseBuffers.delete(messageId);
    this.artifactIds.delete(messageId);
  }

  applyArtifact(envelope: IAyaEnvelope, placeholderId: string): void {
    const payload = envelope.payload;
    const content = payload.content;
    if (!content || typeof content !== 'object' || Array.isArray(content)) {
      return;
    }
    const artifact = content as Record<string, unknown>;
    if (payload.kind === 'reference') {
      if (
        payload.placement !== 'user' ||
        typeof payload.client_message_id !== 'string'
      ) {
        return;
      }
      const confirmed = toConfirmedReference(artifact);
      if (!confirmed) {
        return;
      }
      this.messagesStore.patchById(payload.client_message_id, (message) => {
        const references = message.references ?? [];
        const local = references.find((r) => r.id === confirmed.id);
        const merged =
          isKnowledgeDocumentReference(confirmed) && local && isKnowledgeDocumentReference(local)
            ? {
              ...confirmed,
              originalFilename: confirmed.originalFilename ?? local.originalFilename,
              detectedMime: confirmed.detectedMime ?? local.detectedMime
            }
            : confirmed;
        return {
          id: envelope.messageId || message.id,
          references: local
            ? references.map((r) => (r === local ? merged : r))
            : [...references, merged]
        };
      });
      return;
    }
    if (payload.kind === 'user_attachment') {
      this.applyUserAttachment(envelope, artifact);
      return;
    }
    if (payload.placement !== 'agent') {
      return;
    }
    const messageId = envelope.messageId || placeholderId;
    if (this.messagesStore.findIndexById(messageId) === -1) {
      this.messagesStore.assignServerId(placeholderId, messageId);
    }
    const ids = this.artifactIds.get(messageId) || new Set<string>();
    const artifactId =
      typeof payload.artifact_id === 'string' ? payload.artifact_id : undefined;
    if (artifactId && ids.has(artifactId)) {
      return;
    }
    const normalized =
      payload.kind === 'attachment' ? normalizeAttachment(artifact) : null;
    const attachment =
      normalized && isAgentAttachment(normalized) ? normalized : null;
    const chart = payload.kind === 'chart' ? normalizeChart(artifact) : null;
    if (!attachment && !chart) {
      return;
    }
    const applied = this.messagesStore.patchById(messageId, (message) => ({
      ...(attachment && {
        attachments: message.attachments?.some(
          (a) => isAgentAttachment(a) && a.fileId === attachment.fileId
        )
          ? message.attachments
          : [...(message.attachments || []), attachment]
      }),
      ...(chart && { charts: [...(message.charts || []), chart] })
    }));
    if (applied && artifactId) {
      ids.add(artifactId);
      this.artifactIds.set(messageId, ids);
    }
  }

  /** Não sobrescreve um parcial com conteúdo: o texto recebido continua visível. */
  showAgentError(errorMessage: string, messageId?: string): void {
    const target = messageId
      ? this.messagesStore
          .getMessages()
          .find((m) => m.id === messageId || m.localId === messageId)
      : undefined;
    const parcialVazio =
      target?.role === EnumMessageRole.AGENT && !target.content;

    if (parcialVazio) {
      this.messagesStore.patchById(target.localId, () => ({
        content: errorMessage,
        htmlContent: errorMessage,
        isError: true
      }));
    } else {
      this.messagesStore.add({
        localId: generateLocalId(),
        role: EnumMessageRole.AGENT,
        content: errorMessage,
        htmlContent: errorMessage,
        isError: true,
        timestamp: new Date()
      });
    }
  }

  sendUserMessage(message: IChatInputMessage): string {
    const localId = generateLocalId();
    const userMessage: IChatMessage = {
      localId,
      role: EnumMessageRole.USER,
      content: message.text,
      quickActionTitle: message.quickActionTitle,
      references: toOptionalReferences(message.selectedKnowledgeDocument, message.selectedInformation),
      ...(message.attachments?.length && { attachments: [...message.attachments] }),
      timestamp: new Date()
    };
    this.messagesStore.add(userMessage);
    return localId;
  }

  addAgentPlaceholder(): string {
    const localId = generateLocalId();
    this.messagesStore.add({
      localId,
      role: EnumMessageRole.AGENT,
      content: '',
      timestamp: new Date()
    });
    return localId;
  }

  adoptResponse(responseMessageId: string): string {
    if (this.messagesStore.findIndexById(responseMessageId) !== -1) {
      return responseMessageId;
    }

    const localId = generateLocalId();
    this.messagesStore.add({
      id: responseMessageId,
      localId,
      role: EnumMessageRole.AGENT,
      content: '',
      timestamp: new Date()
    });
    return responseMessageId;
  }

  resolveApproval(current: IHITLRequest, decisions: IHITLDecision[]): string[] {
    const resolvedStatus =
      decisions[0].type === EnumHITLDecisionType.REJECT
        ? EnumHITLStatus.CANCELLED
        : EnumHITLStatus.RESOLVED;
    const resolvedCardId = generateLocalId();
    this.messagesStore.add({
      localId: resolvedCardId,
      role: EnumMessageRole.AGENT,
      content: '',
      timestamp: new Date(),
      interrupt: { ...current, status: resolvedStatus },
      interruptResolved: true
    });

    const decisionBubbleId = generateLocalId();
    this.messagesStore.add({
      localId: decisionBubbleId,
      role: EnumMessageRole.USER,
      content: this.describeDecisions(decisions),
      timestamp: new Date()
    });

    return [resolvedCardId, decisionBubbleId];
  }

  private bufferFor(
    envelope: IAyaEnvelope,
    placeholderId: string
  ): ResponseBuffer {
    const messageId = envelope.messageId || placeholderId;
    if (this.messagesStore.findIndexById(messageId) === -1) {
      this.messagesStore.assignServerId(placeholderId, messageId);
    }
    let buffer = this.responseBuffers.get(messageId);
    if (!buffer) {
      buffer = new ResponseBuffer(messageId, this.markdownFormatter);
      this.responseBuffers.set(messageId, buffer);
    }
    return buffer;
  }

  /** Casa pelo `client_message_id`: o id otimista ainda não virou o persistido. */
  private applyUserAttachment(
    envelope: IAyaEnvelope,
    artifact: Record<string, unknown>
  ): void {
    const payload = envelope.payload;
    if (
      payload.placement !== 'user' ||
      typeof payload.client_message_id !== 'string'
    ) {
      return;
    }
    const confirmed = normalizeAttachment(artifact);
    if (!confirmed || !isUserAttachment(confirmed)) {
      return;
    }
    const messageId =
      typeof payload.message_id === 'string' && payload.message_id
        ? payload.message_id
        : envelope.messageId;
    this.messagesStore.patchById(payload.client_message_id, (message) => {
      const current = message.attachments || [];
      // O reaproveitado entra como artefato e volta como anexo do usuário.
      const index = current.findIndex(
        (a) => a.attachmentId === confirmed.attachmentId
      );
      const attachments = [...current];
      if (index === -1) {
        attachments.push(confirmed);
      } else {
        attachments[index] = confirmed;
      }
      return {
        ...(messageId && { id: messageId }),
        attachments
      };
    });
  }

  private describeDecisions(decisions: IHITLDecision[]): string {
    return this.describeDecision(decisions[0]);
  }

  private describeDecision(decision: IHITLDecision): string {
    if (decision.type === EnumHITLDecisionType.REJECT) {
      return decision.message ? `Cancelar: ${decision.message}` : 'Cancelar';
    }
    return 'Aprovar';
  }
}
